import * as THREE from 'three';

/**
 * Loose hair moves like the cloak does. The free ends of a tail, a plait or long hair swing with the body's acceleration, turns and
 * blows, and ripple a little as it walks. It is done the way the cloth's leg push is (see `cloth-collision.ts`): each hair vertex carries
 * a `hairSway` weight, from 0 where the hair is held to the head up to 1 at its free end, and the vertex shader moves it after skinning
 * by the animator's springs (`CharacterAnimator.swayHair`), so the hair stays one skinned draw with the rest of the head and costs no CPU.
 *
 * The weight is signed: hair lying behind the body swings back and forth and from side to side; hair lying in front (locks over the
 * chest) has a negative weight and swings only from side to side, since the chest is in its way.
 */
export const HAIR_SWAY = 'hairSway';
/** How far the loosest hair swings in total at the extreme of the springs, in rig units. */
export const HAIR_SWAY_REACH = .06;

export interface HairSway {
  /** Move the hair: sideways, back (positive swings it back), the ripple's size and the clock it runs on. */
  set(side: number, back: number, ripple: number, time: number): void;
  /** The current sideways and backward swing and ripple, for tests. */
  readonly state: { side: number; back: number; ripple: number };
  /** Add the sway to a material; every hair surface of one body shares it and its uniforms. */
  apply(material: THREE.Material): void;
}

export function hairSway(seed = 0): HairSway {
  const motion = { value: new THREE.Vector4() }, state = { side: 0, back: 0, ripple: 0 };
  return {
    state,
    set(side, back, ripple, time) {
      const reach = (value: number) => THREE.MathUtils.clamp(value, -HAIR_SWAY_REACH, HAIR_SWAY_REACH);
      state.side = reach(side); state.back = reach(back); state.ripple = ripple;
      motion.value.set(state.side, state.back, ripple, time + seed);
    },
    apply(material) {
      material.customProgramCacheKey = () => 'hair-sway';
      material.onBeforeCompile = shader => {
        shader.uniforms.uHair = motion;
        shader.vertexShader = `attribute float ${HAIR_SWAY};\nuniform vec4 uHair;\n` + shader.vertexShader.replace('#include <skinning_vertex>', `#include <skinning_vertex>
        if (${HAIR_SWAY} != 0.) {
          float free = abs(${HAIR_SWAY}), swing = free * free, behind = step(0., ${HAIR_SWAY});
          float ripple = sin(uHair.w * 4.2 - free * 5.5 + position.x * 9.) * uHair.z;
          transformed += vec3(uHair.x, 0., -(uHair.y + ripple) * behind) * swing;
        }`);
      };
    },
  };
}
